import React, { useState, useEffect } from 'react';
import {
    Modal,
    Button,
    Form,
    Row,
    Col,
    Alert
} from 'react-bootstrap';
import './FileUploadModal.css';
import instance from '../../services/baseService';
import useSidebar from '../../hooks/useSidebar';
import { showLoader } from "../../slices/commonReducer";
import { useDispatch } from "react-redux";

const BRCFileUploadModal = ({ show, onHide, clientId, reFetch }) => {
    const [startDate, setStartDate] = useState(() => {
        const today = new Date();
        const currentMonth = today.getMonth(); // 0-11 (0 = January)
        const currentYear = today.getFullYear();
        // If current month is before April (0-2 = Jan-Mar), use previous year
        const financialYear = currentMonth < 3 ? currentYear - 1 : currentYear;
        return `${financialYear}-04-01`;
    });

    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { collapse, expand, isSidebarCollapsed } = useSidebar();
    const dispatch = useDispatch();
    const startDateRef = React.useRef(null);
    const endDateRef = React.useRef(null);
    // Validate dates
    const validateDates = () => {
        const newErrors = {};

        if (!startDate) {
            newErrors.startDate = 'Start date is required';
        }

        if (!endDate) {
            newErrors.endDate = 'End date is required';
        }

        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            newErrors.endDate = 'End date must be after start date';
        }

        return newErrors;
    };

    // Handle form submission with API integration
    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setSuccess('');

        const dateErrors = validateDates();
        const newErrors = { ...dateErrors };

        if (!clientId) {
            newErrors.submit = 'Client ID is required for upload';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsLoading(true);

        try {
            // Create FormData for API request
            const formData = new FormData();
            formData.append('start_date', startDate);
            formData.append('end_date', endDate);
            formData.append('process_type', "session_generation");
            // Make API call
            const response = await instance.post(
                `/api/v1/reconciliation/import/${clientId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            // Handle response based on status
            if (response.data && response.data.type === 'success') {
                dispatch(showLoader('Running automation task...'));
                const { data } = response.data;
                switch (data.status) {
                    case 1: // success
                        setSuccess('Automation Started');
                        break;
                    case 2: // processing
                        setSuccess('Automation Started');
                        break;
                    case 5: // success_read
                        setSuccess('Automation Started');
                        break;
                    default:
                        setSuccess('Automation Started');
                }

                // Wait a moment to show success message
                await new Promise(resolve => setTimeout(resolve, 2500));
                await reFetch();
                resetForm();
                onHide(true, data);

            } else {
                setErrors({ submit: response.data?.message || 'Automation failed. Please try again.' });
            }

        } catch (error) {
            console.error('Automation error:', error);

            // Handle different error types
            // if (error.response) {
            //     const { status, data } = error.response;

            //     if (data && data.data && data.data.status) {
            //         // Handle specific status codes from API
            //         switch (data.data.status) {
            //             case 3: // failed
            //                 setErrors({ submit: 'Automation failed.' });
            //                 break;
            //             case 4: // validation error
            //                 setErrors({ submit: 'Validation failed' });
            //                 break;
            //             case 6: // reconciliation api failed
            //                 setErrors({ submit: 'Reconciliation process failed. Please try again later.' });
            //                 break;
            //             default:
            //                 setErrors({ submit: data.message || 'Automation failed. Please try again.' });
            //         }
            //     } else if (status === 400) {
            //         setErrors({ submit: 'Invalid  data.' });
            //     }  else if (status >= 500) {
            //         setErrors({ submit: 'Server error. Please try again later.' });
            //     } else {
            //         setErrors({ submit: data?.message || 'Automation failed. Please try again.' });
            //     }
            // } else if (error.request) {
            //     setErrors({ submit: 'Network error. Please check your connection and try again.' });
            // } else {
            //     setErrors({ submit: 'Automation failed. Please try again.' });
            // }
        } finally {
            setIsLoading(false);
        }
    };

    // Reset form - Modified for single file
    const resetForm = () => {
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();

        const financialYear = currentMonth < 3 ? currentYear - 1 : currentYear;
        const startDateValue = `${financialYear}-04-01`;
        setStartDate(startDateValue);
        setEndDate(new Date().toISOString().split('T')[0]);
        setErrors({});
        setSuccess('');
    };

    // Handle modal close
    const handleClose = () => {
        if (!isLoading) {
            resetForm();
            onHide(false); // Pass false to indicate no upload
            expand();
        }
    };

    // Clear states when modal is closed
    useEffect(() => {
        if (!show) {
            // Reset form when modal is hidden
            resetForm();
        }
    }, [show]);

    // Format file size
    // const formatFileSize = (bytes) => {
    //     if (bytes === 0) return '0 Bytes';
    //     const k = 1024;
    //     const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    //     const i = Math.floor(Math.log(bytes) / Math.log(k));
    //     return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    // };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            centered
            backdrop="static"
        >
            <Modal.Header closeButton className="border-0 d-flex justify-content-between align-items-start">
                <div className="flex-grow-1 text-start">
                    <Modal.Title className="fw-bold">Automate Reconciliation</Modal.Title>
                    <p className="text-muted mb-0">Select Start date and End date to reconcile.</p>
                </div>
            </Modal.Header>
            <Modal.Body className="p-3">
                <Form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <h5 className="fw-semibold mb-3">Select shipping bill period</h5>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-medium">Start Date</Form.Label>
                                    <Form.Control
                                        ref={startDateRef}
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => {
                                            setStartDate(e.target.value);
                                            // Clear start date error when user types
                                            if (errors.startDate) {
                                                setErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.startDate;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => startDateRef.current?.showPicker()}
                                        isInvalid={!!errors.startDate}
                                        disabled={isLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.startDate}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-medium">End Date</Form.Label>
                                    <Form.Control
                                        ref={endDateRef}
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => {
                                            setEndDate(e.target.value);
                                            // Clear end date error when user types
                                            if (errors.endDate) {
                                                setErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.endDate;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => endDateRef.current?.showPicker()}
                                        isInvalid={!!errors.endDate}
                                        disabled={isLoading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.endDate}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                    {/* Upload Section */}
                    {/* <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-semibold mb-0">Upload Icegate File</h5>
                            <a
                                href="/assets/files/ICEGATE data.xlsx"
                                download="ICEGATE data.xlsx"
                                className="btn btn-link text-decoration-none p-0"
                            >
                                Download Sample
                            </a>
                        </div>
                        <div
                            className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => !isUploading && fileInputRef.current?.click()}
                        >
                            <div className="upload-icon">
                                <BsCloudArrowUp />
                            </div>
                            <h6 className="mb-2">
                                <span className="text-primary text-decoration-none">Upload file</span>
                                <span className="text-muted"> or drag and drop</span>
                            </h6>
                            <small className="text-muted">XLS, XLSX up to 10MB</small>
                            <Form.Control
                                ref={fileInputRef}
                                type="file"
                                accept=".xls,.xlsx"
                                onChange={(e) => {
                                    handleFileSelect(e.target.files);
                                    // reset input so selecting the same file again triggers onChange
                                    e.target.value = "";
                                }}
                                className="d-none"
                                disabled={isUploading}
                            />
                        </div>

                        {errors.file && (
                            <Alert variant="danger" className="mt-2 " dismissible
                                onClose={() => setErrors(prev => ({ ...prev, file: null }))}
                            >
                                {errors.file}
                            </Alert>
                        )}
                    </div> */}

                    {/* Selected File Display - Modified for single file */}
                    {/* {file && (
                        <div className="mb-4">
                            <h6 className="fw-semibold mb-3">Selected File</h6>
                            <div className="selected-files">
                                <div className="file-item">
                                    <div className="d-flex align-items-center">
                                        <FiFile className="text-primary me-2" />
                                        <div className="flex-grow-1">
                                            <div className="file-name">{file.name}</div>
                                            <small className="text-muted">
                                                {formatFileSize(file.size)}
                                            </small>
                                        </div>
                                        {!isUploading && (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-danger p-1"
                                                onClick={removeFile}
                                                title="Remove file"
                                            >
                                                <FiX />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )} */}

                    {/* Success Message */}
                    {success && (
                        <Alert variant="success" className="py-2 mb-3">
                            {success}
                        </Alert>
                    )}

                    {/* Error Messages */}
                    {errors.submit && (
                        <Alert variant="danger" className="py-2 mb-3">
                            {errors.submit}
                        </Alert>
                    )}

                    {/* Progress Section */}
                    {/* {isUploading && (
                        <div className="progress-section mb-4">
                            <h6 className="fw-semibold mb-3">Upload Progress</h6>
                            <ProgressBar
                                now={uploadProgress}
                                className="custom-progress mb-2"
                            />
                            <small className="text-muted">{uploadProgress}% complete</small>
                        </div>
                    )} */}
                </Form>
            </Modal.Body>

            <Modal.Footer className='border-0'>
                <Button
                    variant="secondary"
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <Button
                    id='generate_section_id'
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isLoading || !startDate || !endDate}
                    className="d-flex align-items-center"
                >
                    {isLoading ? (
                        <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            Starting...
                        </>
                    ) : (
                        'Start Recon'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default BRCFileUploadModal;